// src/pages/ScanCostPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat, Result } from "@zxing/library";
import { apiUrl } from "../config/api";

/* ======================== Types ======================== */
type Product = {
  code: string;
  name: string;
  unit?: string;
  imageUrl?: string;
  stockOnHand?: number;
};

type LatestCost = {
  unitCost: number | null;        // null = ไม่มี movement
  createdAt?: string | null;
};

/* ================= Autofocus / Camera helpers ================= */
async function enableContinuousAutofocus(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("continuous")) {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
  } else if (modes.includes("auto")) {
    await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
  }
}
async function refocusOnce(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("single-shot")) {
    await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] } as any);
    if (modes.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
    } else if (modes.includes("auto")) {
      await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
    }
  } else {
    const s = track.getSettings?.();
    const w = s?.width || 1280;
    await track.applyConstraints({ width: { ideal: w - 1 } } as any);
    await track.applyConstraints({ width: { ideal: w } } as any);
  }
}
async function tapToFocus(
  track: MediaStreamTrack,
  videoEl: HTMLVideoElement,
  clientX: number,
  clientY: number
) {
  const rect = videoEl.getBoundingClientRect();
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;
  try {
    await track.applyConstraints({
      advanced: [{ pointsOfInterest: [{ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }] }],
    } as any);
    await refocusOnce(track);
  } catch {}
}
async function trySetZoom(track: MediaStreamTrack, z: number) {
  const caps: any = track.getCapabilities?.() || {};
  if (caps.zoom) {
    const v = Math.max(caps.zoom.min, Math.min(caps.zoom.max, z));
    await track.applyConstraints({ advanced: [{ zoom: v }] } as any);
  }
}
async function bumpExposure(track: MediaStreamTrack, step = 0.3) {
  const caps: any = track.getCapabilities?.() || {};
  if (typeof caps.exposureCompensation?.min === "number") {
    const cur: any = track.getSettings?.() || {};
    const next = Math.max(
      caps.exposureCompensation.min,
      Math.min(caps.exposureCompensation.max, (cur.exposureCompensation ?? 0) + step)
    );
    await track.applyConstraints({ advanced: [{ exposureCompensation: next }] } as any);
  }
}
async function waitVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return;
  await new Promise<void>((resolve) => {
    const cb = () => {
      if (video.videoWidth > 0) {
        video.removeEventListener("loadeddata", cb);
        resolve();
      }
    };
    video.addEventListener("loadeddata", cb);
  });
}
/* ======================================================= */

export default function ScanCostPage(): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ensureCanvas = () => {
    if (!workCanvasRef.current) workCanvasRef.current = document.createElement("canvas");
    return workCanvasRef.current!;
  };

  const scanLoopRef = useRef<number | null>(null);
  const gotOneRef = useRef<boolean>(false);

  // Readers
  const [readerFast] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
    ]);
    r.setHints?.(hints);
    return r;
  });
  const [readerHard] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.PDF_417,
      BarcodeFormat.AZTEC,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);

  const [scanning, setScanning] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: "" });
  const [resultText, setResultText] = useState<string>("");

  // Product + Cost state
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);

  const [loadingCost, setLoadingCost] = useState(false);
  const [latestCost, setLatestCost] = useState<LatestCost>({ unitCost: null, createdAt: null });

  // qty for preview total
  const [qty, setQty] = useState<number>(1);

  // image upload preview
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /* -------------------- Sounds -------------------- */
  const playBeep = () => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
      o.stop(ctx.currentTime + 0.16);
    } catch {}
  };

  /* -------------------- Devices -------------------- */
  useEffect(() => {
    (async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list);
        const back = list.find((d) => /back|environment/i.test(d.label || ""));
        if (!deviceId && list.length) setDeviceId((back ?? list[list.length - 1]).deviceId);
      } catch (e: any) {
        setErrorMsg(e?.message ?? "ไม่สามารถอ่านรายชื่อกล้องได้");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2000);
    return () => clearTimeout(t);
  }, [toast.show]);

  /* -------------------- Camera loop -------------------- */
  const stopScanLoop = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    scanLoopRef.current = null;
    gotOneRef.current = false;
  };
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  const drawROIToCanvas = (video: HTMLVideoElement, scale = 1) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const roiPct = { w: 0.8, h: 0.8 };
    const rw = vw * roiPct.w;
    const rh = vh * roiPct.h;
    const rx = (vw - rw) / 2;
    const ry = (vh - rh) / 2;
    const canvas = ensureCanvas();
    canvas.width = Math.max(1, Math.floor(rw * scale));
    canvas.height = Math.max(1, Math.floor(rh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, rx, ry, rw, rh, 0, 0, canvas.width, canvas.height);
    return canvas;
  };
  async function fastPhase(video: HTMLVideoElement): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      let gotOne = false;
      const controlsPromise = readerFast.decodeFromVideoElement(
        video,
        (result: Result | undefined) => {
          if (gotOne) return;
          if (result) {
            gotOne = true;
            controlsPromise.then((c) => c.stop()).catch(() => {});
            resolve(result.getText().trim());
          }
        }
      );
      setTimeout(() => {
        if (!gotOne) {
          controlsPromise.then((c) => c.stop()).catch(() => {});
          resolve(null);
        }
      }, 800);
    });
  }
  const startScanLoop = () => {
    const video = videoRef.current!;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const hardScales = [1, 1.5, 2];
    let zoom = 1;
    let lastAdj = performance.now();

    const hardStep = async () => {
      if (!scanning || gotOneRef.current) return;
      let decoded: string | null = null;
      for (const s of hardScales) {
        const canvas = drawROIToCanvas(video, s);
        if (!canvas) continue;
        try {
          const res = await readerHard.decodeFromCanvas(canvas);
          const txt = res?.getText?.();
          if (txt) {
            decoded = txt.trim();
            break;
          }
        } catch {}
      }
      if (decoded) {
        await handleDecodedCode(decoded);
        return;
      }
      const now = performance.now();
      if (track && now - lastAdj > 700) {
        lastAdj = now;
        await bumpExposure(track, 0.3).catch(() => {});
        zoom = Math.min(zoom + 0.2, 2.0);
        await trySetZoom(track, zoom).catch(() => {});
      }
      scanLoopRef.current = requestAnimationFrame(hardStep);
    };

    (async () => {
      const quick = await fastPhase(video);
      if (quick) {
        await handleDecodedCode(quick);
        return;
      }
      scanLoopRef.current = requestAnimationFrame(hardStep);
    })();
  };

  useEffect(() => {
    if (!videoRef.current) return;
    if (!scanning) {
      stopScanLoop();
      stopStream();
      return;
    }
    (async () => {
      try {
        setErrorMsg("");
        setResultText("");
        gotOneRef.current = false;

        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } }
            : { facingMode: { ideal: "environment" }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        videoRef.current!.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current!.play();
        await waitVideoReady(videoRef.current!);
        const track = stream.getVideoTracks()[0];
        enableContinuousAutofocus(track).catch(() => {});
        startScanLoop();
      } catch (e: any) {
        setErrorMsg(e?.message ?? "เปิดกล้องไม่ได้ (ต้อง https หรือ localhost)");
        setScanning(false);
      }
    })();
    return () => {
      stopScanLoop();
      stopStream();
    };
  }, [deviceId, scanning]);

  const toggleTorch = async (on: boolean) => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) {
        setErrorMsg("ไม่พบวิดีโอแทร็ก");
        return;
      }
      const caps = (track.getCapabilities?.() as any) as { torch?: boolean };
      if (caps?.torch) {
        await track.applyConstraints({ advanced: [{ torch: on }] } as any);
      } else {
        setErrorMsg("อุปกรณ์นี้ไม่รองรับไฟฉาย");
      }
    } catch {
      setErrorMsg("เปิดไฟฉายไม่สำเร็จ");
    }
  };

  const stopScan = () => {
    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  /* -------------------- Data fetching -------------------- */
  const fetchProduct = async (code: string): Promise<Product | null> => {
    setLoadingProduct(true);
    try {
      const url = new URL(apiUrl("/products"));
      url.searchParams.set("q", code.trim());
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error ?? res.statusText);

      const items: any[] = Array.isArray(data?.items) ? data.items : [];
      const found =
        items.find((it) => String(it.code).toLowerCase() === code.toLowerCase()) ?? items[0];
      if (!found) return null;

      const p: Product = {
        code: found.code,
        name: found.name,
        unit: found.unit ?? undefined,
        imageUrl: found.imageUrl ?? undefined,
        stockOnHand: typeof found.stockOnHand === "number" ? found.stockOnHand : undefined,
      };
      return p;
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ดึงข้อมูลสินค้าไม่สำเร็จ");
      return null;
    } finally {
      setLoadingProduct(false);
    }
  };

  // PMN-CRITICAL(/ScanCostCheckPage): This reads the latest inventory_movement row, not FIFO/current available cost.
  const fetchLatestCost = async (productCode: string): Promise<LatestCost> => {
    setLoadingCost(true);
    try {
      const url = new URL(apiUrl("/inventory_movements"));
      url.searchParams.set("product_code", productCode);
      url.searchParams.set("limit", "1");
      url.searchParams.set("offset", "0");
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error ?? res.statusText);

      const rows: any[] = Array.isArray(data?.items) ? data.items : [];
      if (!rows.length) return { unitCost: null, createdAt: null }; // ❗ ไม่มี movement

      // เอาแถวล่าสุดตาม API (ORDER BY created_at DESC, id DESC)
      const r = rows[0];
      const unitCost = r?.unit_cost != null ? Number(r.unit_cost) : null;
      return { unitCost, createdAt: r?.created_at ?? null };
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ดึงราคาต้นทุนไม่สำเร็จ");
      return { unitCost: null, createdAt: null };
    } finally {
      setLoadingCost(false);
    }
  };

  const handleCode = async (code: string) => {
    const key = code.trim();
    if (!key) return;
    setResultText(key);
    playBeep();
    setErrorMsg("");

    const p = await fetchProduct(key);
    setProduct(p);
    if (!p) return;

    const c = await fetchLatestCost(p.code);
    setLatestCost(c);
  };

  // from scanner
  // PMN-CRITICAL(/ScanCostCheckPage): A successful decode must stop the scan loop and camera stream to avoid duplicate lookups.
  const handleDecodedCode = async (code: string) => {
    if (gotOneRef.current) return;
    gotOneRef.current = true;
    await handleCode(code);
    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  // from image upload
  const loadImageBitmap = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
    if ("createImageBitmap" in window) return await createImageBitmap(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error("โหลดรูปไม่สำเร็จ"));
    });
    return img;
  };
  async function decodeFromImageFile(file: File) {
    try {
      const bitmap = await loadImageBitmap(file);
      const maxSide = 1600;
      const iw = "width" in bitmap ? (bitmap as any).width : (bitmap as HTMLImageElement).naturalWidth;
      const ih = "height" in bitmap ? (bitmap as any).height : (bitmap as HTMLImageElement).naturalHeight;
      const scale = Math.min(1, maxSide / Math.max(iw, ih));
      const cw = Math.max(1, Math.floor(iw * scale));
      const ch = Math.max(1, Math.floor(ih * scale));

      const canvas = ensureCanvas();
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(bitmap as any, 0, 0, cw, ch);

      const tryAngles = [0, 90, 180, 270];
      let decoded: string | null = null;
      for (const ang of tryAngles) {
        if (ang !== 0) {
          const tmp = ensureCanvas();
          const w = (ang % 180 === 0) ? cw : ch;
          const h = (ang % 180 === 0) ? ch : cw;
          tmp.width = w; tmp.height = h;
          const c2 = tmp.getContext("2d")!;
          c2.fillStyle = "#fff";
          c2.fillRect(0, 0, w, h);
          c2.save();
          c2.translate(w / 2, h / 2);
          c2.rotate((ang * Math.PI) / 180);
          c2.drawImage(canvas, -cw / 2, -ch / 2);
          c2.restore();
          try {
            const res = await readerHard.decodeFromCanvas(tmp);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch {}
        } else {
          try {
            const res = await readerHard.decodeFromCanvas(canvas);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch {}
        }
      }
      if (!decoded) {
        setErrorMsg("อ่าน QR จากรูปไม่สำเร็จ — ลองถ่ายให้ชัด/สว่างขึ้น หรือครอปให้ใกล้ QR");
        return;
      }
      await handleCode(decoded);
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ประมวลผลไฟล์ภาพไม่สำเร็จ");
    }
  }

  /* ======================== UI ======================== */
  const totalCost =
  latestCost.unitCost != null && Number.isFinite(latestCost.unitCost)
    ? Math.max(0, latestCost.unitCost * Math.max(1, qty))   // ไม่ floor
    : null;

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4 py-md-5">
        {/* Toast */}
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
          {toast.show && (
            <div className="alert alert-success shadow mb-0 rounded-3 py-2 px-3">{toast.message}</div>
          )}
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">สแกน/พิมพ์โค้ด • เช็คราคาต้นทุน</h4>

                

                {/* Camera */}
                <div className="ratio ratio-4x3 bg-secondary rounded-3 mb-3 d-flex align-items-center justify-content-center">
                  <video
                    ref={videoRef}
                    className="w-100 h-100 rounded-3"
                    muted
                    playsInline
                    autoPlay
                    onClick={(e) => {
                      const track = streamRef.current?.getVideoTracks?.()[0];
                      if (!track || !videoRef.current) return;
                      tapToFocus(track, videoRef.current, e.clientX, e.clientY).catch(() => {});
                    }}
                  />
                </div>

                <div className="row gy-2 align-items-center mb-3">
                  <div className="col-12 col-md-6">
                    <select
                      className="form-select"
                      value={deviceId ?? ""}
                      onChange={(e) => setDeviceId(e.target.value)}
                    >
                      {devices.map((d, idx) => (
                        <option key={d.deviceId || idx} value={d.deviceId}>
                          {d.label || `Camera ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6 d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary flex-fill" onClick={() => toggleTorch(true)}>เปิดไฟ</button>
                    <button type="button" className="btn btn-outline-secondary flex-fill" onClick={() => toggleTorch(false)}>ปิดไฟ</button>
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-fill"
                      onClick={async () => {
                        const track = streamRef.current?.getVideoTracks?.()[0];
                        if (!track) { setErrorMsg("ยังไม่พบกล้อง/สตรีม"); return; }
                        await refocusOnce(track).catch(() => {});
                      }}
                    >
                      โฟกัสใหม่
                    </button>
                  </div>
                </div>

                <div className="d-grid gap-2 mb-3">
                  <button type="button" className="btn btn-success btn-lg" disabled={scanning} onClick={() => setScanning(true)}>
                    {scanning ? "กำลังสแกน..." : "▶️ สแกนอีกครั้ง"}
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={stopScan}>🛑 หยุดกล้อง</button>
                </div>

                {resultText && (
                  <div className="alert alert-info rounded-3 py-2 mb-3">โค้ดล่าสุด: <span className="fw-semibold">{resultText}</span></div>
                )}
                {loadingProduct && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังดึงข้อมูลสินค้า…</span>
                  </div>
                )}

                {/* Input manual code */}
                <form
                  className="row g-2 align-items-center mb-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const input = (e.currentTarget.querySelector("#codeInput") as HTMLInputElement) || null;
                    const v = input?.value.trim() || "";
                    if (!v) return;
                    handleCode(v);
                  }}
                >
                  <div className="col-12">
                    <label htmlFor="codeInput" className="form-label mb-1">รหัส (พิมพ์แทนการสแกน)</label>
                    <input id="codeInput" className="form-control" placeholder="PD1 / 885... / ข้อความ" />
                  </div>
                  <div className="col-12 d-flex gap-2">
                    <button type="submit" className="btn btn-primary flex-fill">ค้นหา</button>
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-fill"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      📷 ถ่าย/อัปโหลดรูป QR
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="d-none"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) decodeFromImageFile(f);
                        e.currentTarget.value = "";
                      }}
                    />
                  </div>
                </form>

                {/* Product + Cost card */}
                {product && (
                  <div className="card border rounded-3 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="d-flex gap-3">
                        {product.imageUrl && (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12 }}
                          />
                        )}
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="small text-secondary">รหัสสินค้า</div>
                              <div className="fw-semibold">{product.code}</div>
                            </div>
                            <div className="text-end">
                              <div className="small text-secondary">ราคาต้นทุน</div>
                              <div className="fs-5 fw-bold text-primary">
                                {loadingCost ? "…" : latestCost.unitCost != null
                                  ? `฿${latestCost.unitCost.toFixed(2)}`
                                  : "ไม่มี"}
                              </div>
                            </div>
                          </div>
                          <div className="fw-semibold mt-1">{product.name}</div>
                          <div className="small text-muted mt-1">
                            คงเหลือ: {product.stockOnHand ?? "-"} {product.unit ? `· หน่วย: ${product.unit}` : ""}
                          </div>
                        </div>
                      </div>

                      <div className="row g-2 align-items-center mt-3">
                        <div className="col-auto small text-secondary">จำนวน</div>
                        <div className="col-4 col-sm-3 col-md-3">
                          <input
                            type="number"
                            min={1}
                            step={1}
                            className="form-control"
                            value={qty}
                            onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                          />
                        </div>
                      </div>

                      <div className="border border-primary rounded-3 bg-white shadow-sm mt-3 px-3 py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="small text-black">รวมราคาต้นทุน ({qty}x)</div>
                          <div className="fs-4 fw-bold">
                            {latestCost.unitCost != null ? (
                                (latestCost.unitCost * qty).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })
                                ) : (
                            "-"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                <div className="text-center mt-3">
                  <a href="/" className="btn btn-link text-decoration-none">← กลับหน้าเมนู</a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * ต้องเปิดผ่าน <code>https</code> หรือ <code>http://localhost</code> เพื่อให้กล้องทำงาน โดยเฉพาะบนมือถือ
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
